/**
 * VolumetricHologram — turns a 2D character image into a particle cloud
 * rendered in 3D space, rotates with cursor parallax, audio-reactive Z-pulse.
 *
 *  - 100% in-browser canvas2D — no Three.js, no WebGL setup.
 *  - Sample the image into ~6500 particles, each with (x,y,z) world coords.
 *  - Camera rotates around Y axis. Mouse moves shift yaw/pitch (parallax).
 *  - Bass amplitude (from optional analyser) pulses each particle's Z
 *    radially, making the hologram "breathe" with the music.
 *  - Pepper's Ghost mode: renders the same scene 4 times (top/right/bottom/
 *    left) rotated 90° each, so the screen reflects through a glass pyramid
 *    placed on it as a real 3D floating figure.
 *
 * Props:
 *   src:        string         character image URL
 *   analyser?:  AnalyserNode   optional Web Audio analyser for audio reactivity
 *   peppersGhost?: boolean     true = 4-quadrant mirrored render
 *   className?: string
 */
import { useEffect, useRef } from "react";

const PARTICLE_TARGET = 6500;
const SAMPLE_STRIDE = 4; // sample every Nth pixel; tuned with PARTICLE_TARGET below

export default function VolumetricHologram({
  src,
  analyser = null,
  peppersGhost = false,
  className = "",
  testId = "volumetric-hologram",
}) {
  const canvasRef = useRef(null);
  const dataRef = useRef({
    particles: [],
    yaw: 0,
    pitch: 0,
    targetYaw: 0,
    targetPitch: 0,
    img: null,
    ready: false,
  });
  const rafRef = useRef(0);
  const freqDataRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(r.width * dpr));
      canvas.height = Math.max(1, Math.floor(r.height * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Sample image into particles
    const img = new Image();
    // Always request CORS-safe pixels — backend serves Access-Control-Allow-Origin: *
    img.crossOrigin = "anonymous";
    img.onerror = (e) => {
      console.warn("VolumetricHologram: image failed to load", src, e?.type);
    };
    img.onload = () => {
      const off = document.createElement("canvas");
      // Downscale for sampling (smaller = fewer particles but faster)
      const targetW = 96;
      const aspect = img.naturalHeight / img.naturalWidth || 1;
      const targetH = Math.round(targetW * aspect);
      off.width = targetW;
      off.height = targetH;
      const octx = off.getContext("2d");
      octx.drawImage(img, 0, 0, targetW, targetH);
      let imageData;
      try {
        imageData = octx.getImageData(0, 0, targetW, targetH).data;
      } catch (err) {
        // CORS prevented pixel access — fall back gracefully.
        console.warn("VolumetricHologram: pixel sampling blocked by CORS", err);
        return;
      }
      const stride = Math.max(1, Math.floor(Math.sqrt((targetW * targetH) / PARTICLE_TARGET)) || SAMPLE_STRIDE);
      const particles = [];
      for (let y = 0; y < targetH; y += stride) {
        for (let x = 0; x < targetW; x += stride) {
          const i = (y * targetW + x) * 4;
          const r = imageData[i];
          const g = imageData[i + 1];
          const b = imageData[i + 2];
          const a = imageData[i + 3];
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          if (a < 40 || lum < 0.02) continue; // skip background
          // Centered world coords in [-1, 1]
          const wx = (x / targetW) * 2 - 1;
          const wy = -((y / targetH) * 2 - 1) * aspect;
          // Z derived from luminance — bright pixels closer to camera
          const wz = (lum - 0.5) * 0.7;
          particles.push({
            x: wx,
            y: wy,
            z: wz,
            r,
            g,
            b,
            l: lum,
            // small per-particle phase used for breathing
            ph: Math.random() * Math.PI * 2,
          });
        }
      }
      dataRef.current.particles = particles;
      dataRef.current.img = img;
      dataRef.current.ready = true;
    };
    img.src = src;

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      dataRef.current.targetYaw = (px - 0.5) * 0.7; // ±0.35 rad parallax
      dataRef.current.targetPitch = -(py - 0.5) * 0.35;
    };
    canvas.addEventListener("mousemove", onMove);

    if (analyser) {
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
    }

    const renderScene = (offsetX, offsetY, scale, rotation, time, bass, treble) => {
      const data = dataRef.current;
      if (!data.ready) return;
      const w = canvas.width;
      const h = canvas.height;
      const cx = w * offsetX;
      const cy = h * offsetY;
      const radius = Math.min(w, h) * 0.34 * scale;
      const focal = radius * 2.2;

      // Auto-rotate yaw 0.4 rad/sec + parallax
      data.yaw += (data.targetYaw - data.yaw) * 0.06;
      data.pitch += (data.targetPitch - data.pitch) * 0.06;
      const baseYaw = data.yaw + time * 0.0004 + rotation;
      const cosY = Math.cos(baseYaw);
      const sinY = Math.sin(baseYaw);
      const cosP = Math.cos(data.pitch);
      const sinP = Math.sin(data.pitch);

      // bass-driven breathing scale
      const breath = 1 + bass * 0.18;

      const particles = data.particles;
      // sort by depth roughly via stride traversal — too costly to fully sort
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        // rotate around Y then pitch
        const rx = p.x * cosY + p.z * sinY;
        const rz1 = -p.x * sinY + p.z * cosY;
        const ry = p.y * cosP - rz1 * sinP;
        const rz = p.y * sinP + rz1 * cosP;
        const z = rz * breath + 1.6; // shift away from camera
        if (z <= 0.01) continue;
        const k = focal / z;
        const sx = cx + rx * radius * k * 0.35;
        const sy = cy + ry * radius * k * 0.35;
        // breathing per-particle pulse
        const pulse = 0.5 + 0.5 * Math.sin(time * 0.003 + p.ph);
        const size = (0.6 + p.l * 1.4 + treble * 1.2) * scale * (pulse * 0.4 + 0.6);
        // hologram colour — pink/cyan tint based on z depth, plus a touch of original tone
        const cyan = Math.min(255, 80 + 175 * (1 - rz) * 0.6);
        const pink = Math.min(255, 80 + 175 * (1 + rz) * 0.6);
        const r = Math.min(255, p.r * 0.35 + pink * 0.65);
        const g = Math.min(255, p.g * 0.35 + cyan * 0.45);
        const b = Math.min(255, p.b * 0.35 + cyan * 0.85);
        const alpha = Math.min(0.95, 0.32 + p.l * 0.55);
        ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(sx, sy, size * dpr, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = (now) => {
      // motion-blur trail
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let bass = 0;
      let treble = 0;
      if (analyser && freqDataRef.current) {
        analyser.getByteFrequencyData(freqDataRef.current);
        const d = freqDataRef.current;
        const n = d.length;
        const bassEnd = Math.floor(n * 0.12);
        const trebleStart = Math.floor(n * 0.55);
        let bs = 0;
        let ts = 0;
        for (let i = 0; i < bassEnd; i++) bs += d[i];
        for (let i = trebleStart; i < n; i++) ts += d[i];
        bass = bs / (bassEnd || 1) / 255;
        treble = ts / (n - trebleStart || 1) / 255;
      } else {
        // gentle auto-pulse so the hologram still breathes without audio
        bass = 0.18 + 0.12 * Math.sin(now * 0.0015);
        treble = 0.1 + 0.08 * Math.sin(now * 0.002 + 1.2);
      }

      if (peppersGhost) {
        // 4-quadrant mirror — top/right/bottom/left, each rotated for a glass pyramid
        renderScene(0.5, 0.25, 0.5, 0, now, bass, treble); // top
        renderScene(0.75, 0.5, 0.5, Math.PI / 2, now, bass, treble); // right
        renderScene(0.5, 0.75, 0.5, Math.PI, now, bass, treble); // bottom
        renderScene(0.25, 0.5, 0.5, -Math.PI / 2, now, bass, treble); // left
      } else {
        renderScene(0.5, 0.5, 1.0, 0, now, bass, treble);
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
    };
  }, [src, analyser, peppersGhost]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={testId}
      className={`block w-full h-full ${className}`}
    />
  );
}
